import React, { useCallback, useRef, useState } from 'react';
import Button from '../../../../components/common/Button/Button';
import ConfirmModal from '../../../../components/common/ConfirmModal/ConfirmModal';
import Icon from '../../../../components/common/Icon/Icon';
import ImageSourcePickerModal from '../../../../components/common/ImageSourcePickerModal/ImageSourcePickerModal';
import PhotoFullscreenModal from '../../../../components/common/PhotoFullscreenModal/PhotoFullscreenModal';
import { STUDENT_BODY_PHOTO_POSITIONS } from '../../../../services/studentPhotoStorage';
import { PhotosFormData, PhotoFile } from '../../../../types/registration';
import { PHOTO_POSITION_TO_FORM_KEY, type PhotoPosition } from '../../../../types/photo';
import { canOfferCameraCapture } from '../../../../utils/canOfferCameraCapture';
import { prepareStudentPhotoFile } from '../../../../utils/imageResize';
import { isAcceptedStudentPhotoFile } from '../../../../utils/studentPhotoFile';
import { useEscapeToClose } from '../../../../hooks/useEscapeToClose';
import {
  BODY_PHOTO_ASSESSMENT_MONTH_LABELS,
  type BodyPhotoAssessmentDateParts,
  bodyPhotoAssessmentDatePartsToIso,
  clampBodyPhotoAssessmentDateParts,
  formatBodyPhotoAssessmentMonthLabel,
  getBodyPhotoAssessmentDayOptions,
  getBodyPhotoAssessmentYearOptions,
  isValidBodyPhotoAssessmentDateParts,
} from '../../../../utils/studentBodyPhotoAssessmentDateFields';
import styles from '../StudentRegister.module.css';

export const PHOTO_POSITION_LABELS: Record<PhotoPosition, string> = {
  front: 'Frontal',
  back: 'Costas',
  right: 'Lado Direito',
  left: 'Lado Esquerdo',
};

export const PHOTO_POSITIONS = STUDENT_BODY_PHOTO_POSITIONS.map((position) => ({
  key: PHOTO_POSITION_TO_FORM_KEY[position],
  label: PHOTO_POSITION_LABELS[position],
}));

const MAX_FILE_SIZE = 15 * 1024 * 1024;

interface AddBodyPhotosModalProps {
  open: boolean;
  data: PhotosFormData;
  assessmentDateParts: BodyPhotoAssessmentDateParts;
  onAssessmentDatePartsChange: (parts: BodyPhotoAssessmentDateParts) => void;
  onPhotoChange: (position: keyof PhotosFormData, photo: PhotoFile | null) => void;
  onMarkStoredPhotoForRemoval?: (position: keyof PhotosFormData) => void;
  disabled?: boolean;
  canSave?: boolean;
  onSave?: () => Promise<void>;
  onClose: () => void;
}

const AddBodyPhotosModal: React.FC<AddBodyPhotosModalProps> = ({
  open,
  data,
  assessmentDateParts,
  onAssessmentDatePartsChange,
  onPhotoChange,
  onMarkStoredPhotoForRemoval,
  disabled = false,
  canSave = false,
  onSave,
  onClose,
}) => {
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [activePickKey, setActivePickKey] = useState<keyof PhotosFormData | null>(null);
  const [showSourcePicker, setShowSourcePicker] = useState(false);
  const [processingKey, setProcessingKey] = useState<keyof PhotosFormData | null>(null);
  const [fullscreenPreview, setFullscreenPreview] = useState<{ src: string; alt: string } | null>(null);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleDismiss = useCallback(() => {
    if (isSaving) {
      return;
    }
    onClose();
  }, [isSaving, onClose]);

  useEscapeToClose(handleDismiss, open && !fullscreenPreview && !showSaveConfirm && !isSaving);

  const handleFileSelect = async (key: keyof PhotosFormData, file: File) => {
    if (!isAcceptedStudentPhotoFile(file)) {
      window.alert('Envie uma imagem em JPG, PNG, WEBP ou HEIC.');
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      window.alert('A imagem deve ter no máximo 15 MB.');
      return;
    }

    setProcessingKey(key);

    try {
      const existingPhoto = data[key];
      if (existingPhoto?.preview.startsWith('blob:')) {
        URL.revokeObjectURL(existingPhoto.preview);
      }

      const resizedFile = await prepareStudentPhotoFile(file);
      const preview = URL.createObjectURL(resizedFile);
      onPhotoChange(key, { file: resizedFile, preview });
    } catch (error) {
      const message = error instanceof Error
        ? error.message
        : 'Não foi possível processar a imagem selecionada.';
      window.alert(message);
    } finally {
      setProcessingKey(null);
    }
  };

  const handleInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const key = activePickKey;
    const file = event.target.files?.[0];
    event.target.value = '';
    setActivePickKey(null);

    if (file && key) {
      await handleFileSelect(key, file);
    }
  };

  const openPickerForKey = (key: keyof PhotosFormData, source: 'gallery' | 'camera') => {
    setActivePickKey(key);
    const input = source === 'camera' ? cameraInputRef.current : galleryInputRef.current;
    input?.click();
  };

  const requestPick = (key: keyof PhotosFormData) => {
    if (disabled || processingKey === key) {
      return;
    }

    if (canOfferCameraCapture()) {
      setActivePickKey(key);
      setShowSourcePicker(true);
      return;
    }

    openPickerForKey(key, 'gallery');
  };

  const handleDrop = async (key: keyof PhotosFormData, event: React.DragEvent) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      await handleFileSelect(key, file);
    }
  };

  const parsedMonth = Number.parseInt(assessmentDateParts.month, 10);
  const parsedYear = Number.parseInt(assessmentDateParts.year, 10);
  const dayOptions = Number.isInteger(parsedMonth) && Number.isInteger(parsedYear)
    ? getBodyPhotoAssessmentDayOptions(parsedMonth, parsedYear)
    : getBodyPhotoAssessmentDayOptions(1, new Date().getFullYear());
  const yearOptions = getBodyPhotoAssessmentYearOptions();

  const updateAssessmentDatePart = (
    field: keyof BodyPhotoAssessmentDateParts,
    value: string,
  ) => {
    const next = clampBodyPhotoAssessmentDateParts({
      ...assessmentDateParts,
      [field]: value,
    });
    onAssessmentDatePartsChange(next);
  };

  const handleClear = (key: keyof PhotosFormData) => {
    if (disabled || !data[key]) {
      return;
    }

    const photo = data[key];
    if (photo?.preview.startsWith('blob:')) {
      URL.revokeObjectURL(photo.preview);
    }

    onPhotoChange(key, null);
  };

  const handleRequestSave = () => {
    if (disabled || isSaving || !onSave) {
      return;
    }

    if (!canSave) {
      window.alert('Selecione fotos ou alterações para salvar antes de enviar.');
      return;
    }

    if (!isValidBodyPhotoAssessmentDateParts(assessmentDateParts)) {
      window.alert('Informe uma data válida para as fotos (dia, mês e ano).');
      return;
    }

    setShowSaveConfirm(true);
  };

  const handleConfirmSave = async () => {
    if (!onSave) {
      return;
    }

    setIsSaving(true);
    try {
      await onSave();
      setShowSaveConfirm(false);
      onClose();
    } catch (error) {
      window.alert(error instanceof Error ? error.message : 'Não foi possível enviar as fotos corporais.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!open) {
    return null;
  }

  return (
    <>
      <div className={styles.photoUploadOverlay} role="presentation" onMouseDown={handleDismiss}>
        <section
          className={styles.photoUploadModal}
          role="dialog"
          aria-modal="true"
          aria-labelledby="add-body-photos-modal-title"
          onMouseDown={(event) => event.stopPropagation()}
        >
          <header className={styles.photoUploadModalHeader}>
            <h3 id="add-body-photos-modal-title" className={styles.photoUploadModalTitle}>
              Adicionar fotos corporais
            </h3>
            <button
              type="button"
              className={styles.photoUploadModalClose}
              onClick={handleDismiss}
              aria-label="Fechar"
              disabled={isSaving}
            >
              <Icon name="close" size={22} />
            </button>
          </header>

          <div className={styles.photoUploadModalBody}>
            <p className={styles.sectionDescription}>
              Selecione ou arraste uma imagem para cada posição. As alterações são confirmadas ao salvar.
            </p>

            <div className={styles.photoAssessmentDateBlock}>
              <span className={styles.label}>Data das fotos</span>
              <div className={styles.photoAssessmentDateRow}>
                <div className={`${styles.photoAssessmentDateField} ${styles.photoAssessmentDateFieldDay}`}>
                  <label className={styles.photoAssessmentDateFieldLabel} htmlFor="body-photo-assessment-day">
                    Dia
                  </label>
                  <select
                    id="body-photo-assessment-day"
                    className={styles.select}
                    value={assessmentDateParts.day}
                    onChange={(event) => updateAssessmentDatePart('day', event.target.value)}
                    disabled={disabled || isSaving}
                  >
                    {dayOptions.map((day) => (
                      <option key={day} value={String(day)}>
                        {day}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={`${styles.photoAssessmentDateField} ${styles.photoAssessmentDateFieldMonth}`}>
                  <label className={styles.photoAssessmentDateFieldLabel} htmlFor="body-photo-assessment-month">
                    Mês
                  </label>
                  <select
                    id="body-photo-assessment-month"
                    className={styles.select}
                    value={assessmentDateParts.month}
                    onChange={(event) => updateAssessmentDatePart('month', event.target.value)}
                    disabled={disabled || isSaving}
                  >
                    {BODY_PHOTO_ASSESSMENT_MONTH_LABELS.map((_, index) => {
                      const monthValue = String(index + 1);
                      return (
                        <option key={monthValue} value={monthValue}>
                          {formatBodyPhotoAssessmentMonthLabel(index + 1)}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div className={`${styles.photoAssessmentDateField} ${styles.photoAssessmentDateFieldYear}`}>
                  <label className={styles.photoAssessmentDateFieldLabel} htmlFor="body-photo-assessment-year">
                    Ano
                  </label>
                  <select
                    id="body-photo-assessment-year"
                    className={styles.select}
                    value={assessmentDateParts.year}
                    onChange={(event) => updateAssessmentDatePart('year', event.target.value)}
                    disabled={disabled || isSaving}
                  >
                    {yearOptions.map((year) => (
                      <option key={year} value={String(year)}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {bodyPhotoAssessmentDatePartsToIso(assessmentDateParts) && (
                <p className={styles.photoAssessmentDateHint}>
                  As fotos serão agrupadas na data escolhida (útil para importar fotos antigas).
                </p>
              )}
            </div>

            <div className={styles.photoGrid}>
              {PHOTO_POSITIONS.map(({ key, label }) => (
                <div key={key} className={styles.photoCard}>
                  <label className={styles.label}>{label}</label>
                  {data[key] ? (
                    <div className={styles.photoPreviewContainer}>
                      <img
                        src={data[key]!.preview}
                        alt={label}
                        className={styles.photoPreview}
                      />
                      <div className={styles.photoPreviewActions}>
                        <button
                          type="button"
                          className={styles.photoPreviewActionBtn}
                          onClick={() => setFullscreenPreview({
                            src: data[key]!.preview,
                            alt: label,
                          })}
                          title="Ver em tela cheia"
                          aria-label="Ver em tela cheia"
                          disabled={disabled}
                        >
                          <Icon name="fullscreen" size={18} />
                        </button>
                        <button
                          type="button"
                          className={styles.photoPreviewActionBtn}
                          onClick={() => requestPick(key)}
                          title="Trocar foto"
                          aria-label="Trocar foto"
                          disabled={disabled || processingKey === key}
                        >
                          <Icon name="swap_horiz" size={18} />
                        </button>
                        <button
                          type="button"
                          className={`${styles.photoPreviewActionBtn} ${styles.photoPreviewActionBtnDanger}`}
                          onClick={() => handleClear(key)}
                          title="Remover foto"
                          aria-label="Remover foto"
                          disabled={disabled}
                        >
                          <Icon name="delete" size={18} />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div
                      className={`${styles.photoDropzone} ${disabled || processingKey === key ? styles.photoDropzoneDisabled : ''}`}
                      onClick={() => requestPick(key)}
                      onDragOver={(event) => {
                        event.preventDefault();
                        event.dataTransfer.dropEffect = 'copy';
                      }}
                      onDrop={(event) => !disabled && processingKey !== key && void handleDrop(key, event)}
                    >
                      <Icon name="cloud_upload" size={32} className={styles.photoDropzoneIcon} />
                      <span className={styles.photoDropzoneText}>
                        {processingKey === key ? 'Processando imagem...' : 'Clique ou arraste uma foto'}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <input
              ref={galleryInputRef}
              type="file"
              accept="image/*"
              onChange={(event) => {
                void handleInputChange(event);
              }}
              className={styles.visuallyHiddenFileInput}
              disabled={disabled}
            />
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(event) => {
                void handleInputChange(event);
              }}
              className={styles.visuallyHiddenFileInput}
              disabled={disabled}
            />
          </div>

          <footer className={styles.photoUploadModalFooter}>
            <Button
              type="button"
              variant="primary"
              size="medium"
              onClick={handleRequestSave}
              disabled={disabled || isSaving || !onSave}
            >
              {isSaving ? 'Salvando...' : 'Salvar'}
            </Button>
          </footer>
        </section>
      </div>

      <ImageSourcePickerModal
        open={showSourcePicker}
        title="Adicionar foto corporal"
        onFilesSelected={(files) => {
          const key = activePickKey;
          setShowSourcePicker(false);
          if (key && files[0]) {
            void handleFileSelect(key, files[0]);
          }
        }}
        onCancel={() => {
          setShowSourcePicker(false);
          setActivePickKey(null);
        }}
      />

      <PhotoFullscreenModal
        open={Boolean(fullscreenPreview)}
        src={fullscreenPreview?.src ?? ''}
        alt={fullscreenPreview?.alt}
        onClose={() => setFullscreenPreview(null)}
      />

      <ConfirmModal
        open={showSaveConfirm}
        title="Salvar fotos corporais"
        message="Deseja salvar as fotos e enviar agora? O envio começa assim que você confirmar."
        confirmLabel="Salvar e enviar"
        cancelLabel="Cancelar"
        variant="info"
        icon="cloud_upload"
        loading={isSaving}
        onConfirm={() => {
          void handleConfirmSave();
        }}
        onCancel={() => {
          if (!isSaving) {
            setShowSaveConfirm(false);
          }
        }}
      />
    </>
  );
};

export default AddBodyPhotosModal;
